import React, { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Separator } from "../../ui/separator";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../ui/select";
import { DollarSign, CreditCard, Package, Plus, Trash2, Percent } from "lucide-react";
import { API } from "../../../App";
import PartNameAutocomplete from "./PartNameAutocomplete";
import { applyPaymentRowUpdate, calculateTotals } from "../../../utils/job-form-utils";

const CompletionSection = ({
  formData, setFormData,
  parts, setParts, payments, setPayments,
  teamMembers, paymentTypes,
  editingJob = null,
  liveTax = null,
  linkedInvoiceFee = null,
}) => {
  const addPart = () => setParts(prev => [...prev, { name: "", cost: "", provider: "company" }]);
  const updatePart = (index, field, value) => {
    const newParts = [...parts];
    newParts[index] = { ...newParts[index], [field]: field === "cost" ? parseFloat(value) || 0 : value };
    setParts(newParts);
  };
  const removePart = (index) => setParts(prev => prev.filter((_, i) => i !== index));

  const addPayment = () => setPayments(prev => [...prev, { payment_type_id: "", amount: "", collected_by: "" }]);
  const updatePayment = (index, field, value) => {
    const newPayments = [...payments];
    // Re-apply the payment type's `default_collected_by` every time the
    // type changes — including when the user picked the wrong type first
    // and is correcting it. The previous behaviour only applied the default
    // on the very first pick (gated on `!collected_by`), which left a stale
    // "Co." / "Tech" choice on the row when switching types.
    // Users can still override afterwards via the Co./Tech toggle button.
    newPayments[index] = applyPaymentRowUpdate(newPayments[index], field, value, paymentTypes);
    setPayments(newPayments);
  };
  const removePayment = (index) => setPayments(prev => prev.filter((_, i) => i !== index));

  // ── Sales tax (mirrors backend core/tax.py) ──
  // Effective rate: the job's store location tax_rate, else the account's
  // Invoice Settings default. A job stamped from a linked invoice shows the
  // invoice's tax snapshot instead (invoice is the source of truth).
  const [taxConfig, setTaxConfig] = useState({ defaultRate: 0, locations: [] });
  const [autoTax, setAutoTax] = useState(false);
  useEffect(() => {
    const token = localStorage.getItem("token");
    const headers = { Authorization: `Bearer ${token}` };
    Promise.all([
      axios.get(`${API}/invoices/template/settings`, { headers }).catch(() => null),
      axios.get(`${API}/store/locations`, { headers }).catch(() => null),
      axios.get(`${API}/tax/settings`, { headers }).catch(() => null),
    ]).then(([s, l, t]) => {
      const locations = Array.isArray(l?.data) ? l.data : (l?.data?.locations || []);
      setTaxConfig({ defaultRate: parseFloat(s?.data?.default_tax_rate) || 0, locations });
      setAutoTax(t?.data?.charge_tax === true);
    });
  }, []);
  const taxLoc = formData.store_location_id
    ? taxConfig.locations.find(l => l.location_id === formData.store_location_id)
    : null;
  // Automatic tax replaces the manual rate: the saved job snapshot (or the
  // live JobTaxPanel below) is the source of truth, so the legacy rate is
  // suppressed to avoid showing two conflicting tax numbers.
  const manualRate = (taxLoc && typeof taxLoc.tax_rate === "number") ? taxLoc.tax_rate : taxConfig.defaultRate;
  // Per-job tax kill switch (Tax panel toggle) — suppresses every tax path.
  const taxDisabled = !!formData.tax_disabled;
  const effectiveTaxRate = (autoTax || taxDisabled) ? 0 : manualRate;
  const invoiceTaxed = !taxDisabled && editingJob?.tax_source === "invoice";
  const snapshotTaxed = invoiceTaxed || (!taxDisabled && autoTax && editingJob?.tax_source === "auto");

  const totals = calculateTotals(formData, parts, payments, teamMembers, paymentTypes, snapshotTaxed ? 0 : effectiveTaxRate);
  // Live automatic tax (unsaved jobs / no snapshot yet): use the preview rate
  // from JobTaxPanel. Tax is always ON TOP of total_charged — commissions and
  // profit stay on the pre-tax amount.
  const liveStatus = liveTax?.status || liveTax?.tax_status;
  const liveRate = liveTax?.rate ?? liveTax?.tax_rate;
  const liveAuto = (!snapshotTaxed && autoTax && liveStatus === "calculated"
    && typeof liveRate === "number" && liveRate > 0) ? liveTax : null;
  const liveTaxAmount = liveAuto ? Math.round(totals.totalCharged * liveRate) / 100 : 0;
  const taxRateDisplay = snapshotTaxed ? (editingJob.tax_rate || 0) : (liveAuto ? liveRate : totals.taxRate);
  const taxAmountDisplay = snapshotTaxed ? (editingJob.tax_amount || 0) : (liveAuto ? liveTaxAmount : totals.taxAmount);
  // Credit fee charged to the customer on the linked invoice — display-only
  // passthrough (never commissioned). Live-fetched invoice wins over the
  // saved job snapshot; total_with_tax from an invoice already includes it.
  const savedFee = invoiceTaxed ? (parseFloat(editingJob?.invoice_credit_fee_amount) || 0) : 0;
  const creditFee = linkedInvoiceFee ? (parseFloat(linkedInvoiceFee.amount) || 0) : savedFee;
  const creditFeeType = linkedInvoiceFee?.type ?? editingJob?.invoice_credit_fee_type;
  const creditFeeValue = linkedInvoiceFee?.value ?? editingJob?.invoice_credit_fee_value;
  const creditFeeLabel = creditFeeType === "percentage" && creditFeeValue
    ? `Credit Fee (${creditFeeValue}%)` : "Credit Fee";
  const customerTotalDisplay = snapshotTaxed
    ? (editingJob.total_with_tax || Math.round((totals.totalCharged + (editingJob.tax_amount || 0) + creditFee) * 100) / 100)
    : Math.round(((liveAuto ? totals.totalCharged + liveTaxAmount : totals.customerTotal) + creditFee) * 100) / 100;
  // Payments should cover what the CUSTOMER owes (incl. tax/fee when they apply).
  const expectedTotal = (taxAmountDisplay > 0 || creditFee > 0) ? customerTotalDisplay : totals.totalCharged;

  return (
    <>
      <Separator className="bg-theme-hover" />
      <div className="space-y-4 p-4 rounded-xl bg-green-500/10 border border-green-500/20">
        <div className="flex items-center gap-2 text-green-400">
          <DollarSign className="w-4 h-4" />
          <span className="text-sm font-medium">Job Completion Details</span>
        </div>

        {/* Total Charged */}
        <div className="space-y-2">
          <Label className="text-muted-foreground text-sm">Total Amount Charged ($)</Label>
          <Input
            type="number" step="0.01" min="0"
            value={formData.total_charged}
            onChange={(e) => setFormData(prev => ({ ...prev, total_charged: e.target.value }))}
            className="input-glass text-lg font-semibold"
            placeholder="0.00"
            data-testid="total-charged-input"
          />
          {!snapshotTaxed && (effectiveTaxRate > 0 || liveAuto) && (
            <p className="text-[10px] text-muted-foreground" data-testid="total-charged-tax-hint">
              Pre-tax amount — {liveAuto ? liveRate : effectiveTaxRate}% tax is added on top for the customer
              {parseFloat(formData.total_charged) > 0 ? ` (customer pays $${(liveAuto ? customerTotalDisplay : totals.customerTotal).toFixed(2)})` : ""}.
            </p>
          )}
        </div>

        {/* Commissions */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2 p-3 rounded-lg bg-black/20 border border-theme">
            <Label className="text-muted-foreground text-sm">Source Commission</Label>
            <div className="flex gap-2">
              <Select value={formData.source_commission_type} onValueChange={(v) => setFormData(prev => ({ ...prev, source_commission_type: v }))}>
                <SelectTrigger className="w-20 input-glass"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="percentage">%</SelectItem>
                  <SelectItem value="fixed">$</SelectItem>
                </SelectContent>
              </Select>
              <Input type="number" step="0.01" value={formData.source_commission_value} onChange={(e) => setFormData(prev => ({ ...prev, source_commission_value: e.target.value }))} className="input-glass flex-1" placeholder="0" />
            </div>
          </div>
          <div className="space-y-2 p-3 rounded-lg bg-black/20 border border-theme">
            <Label className="text-muted-foreground text-sm">Tech Commission</Label>
            <div className="flex gap-2">
              <Select value={formData.technician_commission_type} onValueChange={(v) => setFormData(prev => ({ ...prev, technician_commission_type: v }))}>
                <SelectTrigger className="w-20 input-glass"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="percentage">%</SelectItem>
                  <SelectItem value="fixed">$</SelectItem>
                </SelectContent>
              </Select>
              <Input type="number" step="0.01" value={formData.technician_commission_value} onChange={(e) => setFormData(prev => ({ ...prev, technician_commission_value: e.target.value }))} className="input-glass flex-1" placeholder="0" />
            </div>
          </div>
        </div>

        {/* Payment Methods */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label className="text-muted-foreground text-sm flex items-center gap-2">
              <CreditCard className="w-4 h-4" /> Payment Methods
            </Label>
            <Button type="button" size="sm" variant="ghost" onClick={addPayment} className="text-[#FFCC00] h-7">
              <Plus className="w-3 h-3 mr-1" /> Add Payment
            </Button>
          </div>
          {payments.length === 0 && (
            <p className="text-muted-foreground text-xs">Click &quot;Add Payment&quot; to record how the customer paid</p>
          )}
          {payments.length > 0 && (
            <div className="space-y-2">
              {payments.map((payment, index) => (
                <React.Fragment key={index}>
                  <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center bg-theme-surface p-2 rounded-lg">
                    <Select key={`pt-${paymentTypes.length}`} value={payment.payment_type_id} onValueChange={(v) => updatePayment(index, "payment_type_id", v)}>
                      <SelectTrigger className="input-glass flex-1 min-w-0"><SelectValue placeholder="Payment type" /></SelectTrigger>
                      <SelectContent>
                        {paymentTypes.filter(p => p.is_active).map((pt) => (
                          <SelectItem key={pt.payment_id} value={pt.payment_id}>
                            {pt.name} {pt.processing_fee > 0 && <span className="text-muted-foreground">({pt.processing_fee}% fee)</span>}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <div className="flex gap-2 items-center">
                      <div className="relative flex-1 sm:w-28 sm:flex-none">
                        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                        <Input type="number" step="0.01" value={payment.amount} onChange={(e) => updatePayment(index, "amount", e.target.value)} className="input-glass pl-6" placeholder="0.00" />
                      </div>
                      <button
                        type="button"
                        onClick={() => updatePayment(index, "collected_by", payment.collected_by === "technician" ? "company" : "technician")}
                        className={`flex-shrink-0 h-9 px-2 rounded-md text-xs font-medium border transition-colors ${
                          payment.collected_by === "technician"
                            ? "border-amber-500/40 bg-amber-500/10 text-amber-400 hover:bg-amber-500/20"
                            : "border-border bg-theme-surface text-muted-foreground hover:bg-accent"
                        }`}
                        title={`Collected by: ${payment.collected_by === "technician" ? "Technician" : "Company"}. Click to change.`}
                        data-testid={`payment-collected-by-${index}`}
                      >
                        {payment.collected_by === "technician" ? "Tech" : "Co."}
                      </button>
                      <Button type="button" size="icon" variant="ghost" onClick={() => removePayment(index)} className="text-red-400 h-9 w-9 flex-shrink-0">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  {(() => {
                    const pt = paymentTypes.find(p => p.payment_id === payment.payment_type_id);
                    const selectedTech = formData.assigned_to ? teamMembers.find(m => m.member_id === formData.assigned_to) : null;
                    const chargeProcessing = selectedTech ? selectedTech.charge_processing_fees !== false : true;
                    const fee = (chargeProcessing && pt?.processing_fee > 0) ? ((parseFloat(payment.amount) || 0) * pt.processing_fee / 100) : 0;
                    if (fee > 0) {
                      return (
                        <div className="flex justify-end text-xs text-orange-400 pr-12 -mt-1">
                          Processing fee: ${fee.toFixed(2)} ({pt.processing_fee}%)
                        </div>
                      );
                    }
                    if (!chargeProcessing && pt?.processing_fee > 0) {
                      return (
                        <div className="flex justify-end text-xs text-muted-foreground pr-12 -mt-1">
                          Processing fee waived for this technician
                        </div>
                      );
                    }
                    return null;
                  })()}
                </React.Fragment>
              ))}
            </div>
          )}
        </div>

        {/* Parts Section */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label className="text-muted-foreground text-sm flex items-center gap-2">
              <Package className="w-4 h-4" /> Parts Used
            </Label>
            <Button type="button" size="sm" variant="ghost" onClick={addPart} className="text-[#FFCC00] h-7">
              <Plus className="w-3 h-3 mr-1" /> Add Part
            </Button>
          </div>
          {parts.length === 0 && (
            <p className="text-muted-foreground text-xs">Add parts to track costs and who provided them</p>
          )}
          {parts.length > 0 && (
            <div className="space-y-2">
              <div className="hidden sm:grid grid-cols-12 gap-2 text-xs text-muted-foreground px-1">
                <span className="col-span-5">Part Name</span>
                <span className="col-span-3">Cost</span>
                <span className="col-span-3">Provider</span>
                <span className="col-span-1"></span>
              </div>
              {parts.map((part, index) => (
                <div key={index} className="flex flex-col sm:grid sm:grid-cols-12 gap-2 items-start sm:items-center bg-theme-surface p-2 rounded-lg">
                  <PartNameAutocomplete
                    value={part.name}
                    onChange={(val) => updatePart(index, "name", val)}
                    onSelectItem={(item) => {
                      const newParts = [...parts];
                      newParts[index] = { ...newParts[index], name: item.name, cost: item.unit_price, item_id: item.item_id };
                      setParts(newParts);
                    }}
                  />
                  <div className="relative w-full sm:col-span-3">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                    <Input type="number" step="0.01" value={part.cost} onChange={(e) => updatePart(index, "cost", e.target.value)} className="input-glass pl-6" placeholder="0.00" />
                  </div>
                  <div className="flex gap-2 w-full sm:contents">
                    <Select value={part.provider || "company"} onValueChange={(v) => updatePart(index, "provider", v)}>
                      <SelectTrigger className="input-glass flex-1 sm:col-span-3 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="company">Company</SelectItem>
                        <SelectItem value="technician">Technician</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button type="button" size="icon" variant="ghost" onClick={() => removePart(index)} className="text-red-400 h-9 w-9 sm:col-span-1 flex-shrink-0">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Financial Summary */}
        {(parseFloat(formData.total_charged) > 0 || parts.length > 0 || payments.length > 0) && (
          <div className="mt-4 p-3 rounded-lg bg-muted/40 border border-theme">
            <h4 className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-2">
              <Percent className="w-4 h-4" /> Financial Summary
            </h4>
            <div className="space-y-1.5 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Total Charged:</span>
                <span className="text-foreground font-medium">${totals.totalCharged.toFixed(2)}</span>
              </div>
              {taxAmountDisplay > 0 && (
                <div className="flex justify-between text-muted-foreground" data-testid="job-tax-row">
                  <span>Tax ({taxRateDisplay}%){invoiceTaxed ? " — from linked invoice" : (autoTax ? " — automatic (service address)" : " — added on top")}:</span>
                  <span className="text-teal-600 dark:text-teal-400">+${(taxAmountDisplay || 0).toFixed(2)}</span>
                </div>
              )}
              {creditFee > 0 && (
                <div className="flex justify-between text-muted-foreground" data-testid="job-credit-fee-row">
                  <span>{creditFeeLabel} — charged to customer on invoice:</span>
                  <span className="text-teal-600 dark:text-teal-400">+${creditFee.toFixed(2)}</span>
                </div>
              )}
              {(taxAmountDisplay > 0 || creditFee > 0) && (
                <>
                  <div className="flex justify-between" data-testid="job-customer-total-row">
                    <span className="text-muted-foreground">Customer Total (incl. {[taxAmountDisplay > 0 && "tax", creditFee > 0 && "credit fee"].filter(Boolean).join(" & ")}):</span>
                    <span className="text-foreground font-medium">${(customerTotalDisplay || 0).toFixed(2)}</span>
                  </div>
                  <p className="text-[10px] text-muted-foreground" data-testid="job-tax-note">
                    Commissions and profit are calculated on the pre-tax{creditFee > 0 ? ", pre-fee" : ""} amount — {creditFee > 0 ? "tax and credit fee are" : "tax is"} never commissioned.
                  </p>
                </>
              )}
              {parts.length > 0 && (
                <>
                  <div className="flex justify-between text-muted-foreground">
                    <span>Company Parts:</span>
                    <span className="text-red-600 dark:text-red-400">-${totals.companyPartsCost.toFixed(2)}</span>
                  </div>
                  {totals.techPartsCost > 0 && (
                    <div className="flex justify-between text-muted-foreground">
                      <span>Tech Parts (reimbursed to tech):</span>
                      <span className="text-red-600 dark:text-red-400">-${totals.techPartsCost.toFixed(2)}</span>
                    </div>
                  )}
                </>
              )}
              {totals.sourceCommissionAmount > 0 && (
                <div className="flex justify-between text-muted-foreground">
                  <span>Source Commission ({formData.source_commission_type === "fixed" ? "$" : ""}{formData.source_commission_value}{formData.source_commission_type === "percentage" ? "%" : ""}
                    {totals.sourceExtraFee > 0 ? ` + $${totals.sourceExtraFee.toFixed(2)} fee` : ""}):</span>
                  <span className="text-red-600 dark:text-red-400">-${totals.sourceCommissionAmount.toFixed(2)}</span>
                </div>
              )}
              {totals.techCommissionAmount > 0 && (
                <div className="flex justify-between text-muted-foreground">
                  <span>Tech Commission ({formData.technician_commission_type === "fixed" ? "$" : ""}{formData.technician_commission_value}{formData.technician_commission_type === "percentage" ? "%" : ""}
                    {(() => { const t = formData.assigned_to ? teamMembers.find(m => m.member_id === formData.assigned_to) : null; return t?.parts_deduction === "from_total" ? " after parts" : ""; })()}):
                  </span>
                  <span className="text-red-600 dark:text-red-400">-${totals.techCommissionAmount.toFixed(2)}</span>
                </div>
              )}
              {totals.additionalTechCommission > 0 && (
                <>
                  <div className="flex justify-between text-muted-foreground pl-4">
                    <span className="text-xs">{teamMembers.find(m => m.member_id === formData.assigned_to)?.name || "Primary Tech"}:</span>
                    <span className="text-orange-600 dark:text-orange-400 text-xs">${totals.primaryTechNet.toFixed(2)}</span>
                  </div>
                  {(formData.additional_technician_ids || []).filter(t => t && t.trim()).length > 0 ? (
                    (() => {
                      const extras = (formData.additional_technician_ids || []).filter(t => t && t.trim());
                      const totalTechs = extras.length + 1;
                      const eq = 100 / totalTechs;
                      const isCustom = formData.tech_commission_split_mode === "custom";
                      const isAmountUnit = isCustom && formData.tech_commission_split_unit === "amount";
                      const map = formData.tech_commission_splits_map || {};
                      const amap = formData.tech_commission_splits_amount_map || {};
                      const pctFor = (id) => {
                        if (!isCustom) return eq;
                        const v = parseFloat(map[id]);
                        return isNaN(v) ? eq : v;
                      };
                      const amtFor = (id) => Math.min(parseFloat(amap[id]) || 0, totals.techCommissionAmount);
                      return extras.map((tid) => (
                        <div key={tid} className="flex justify-between text-muted-foreground pl-4">
                          <span className="text-xs">
                            {teamMembers.find(m => m.member_id === tid)?.name || "Tech"}
                            {isAmountUnit ? " (fixed $)" : isCustom ? ` (${Math.round(pctFor(tid) * 100) / 100}%)` : ""}:
                          </span>
                          <span className="text-orange-600 dark:text-orange-400 text-xs">
                            ${(isAmountUnit ? amtFor(tid) : (totals.techCommissionAmount * pctFor(tid)) / 100).toFixed(2)}
                          </span>
                        </div>
                      ));
                    })()
                  ) : (
                    <div className="flex justify-between text-muted-foreground pl-4">
                      <span className="text-xs">{teamMembers.find(m => m.member_id === formData.additional_technician_id)?.name || "2nd Tech"}:</span>
                      <span className="text-orange-600 dark:text-orange-400 text-xs">${totals.additionalTechCommission.toFixed(2)}</span>
                    </div>
                  )}
                </>
              )}
              {totals.processingFees > 0 && (
                <>
                  <div className="flex justify-between text-muted-foreground">
                    <span>Processing Fees:</span>
                    <span className="text-orange-600 dark:text-orange-400">-${totals.processingFees.toFixed(2)}</span>
                  </div>
                  {(totals.techCommissionAmount > 0 || totals.techPartsCost > 0) && (
                    <div className="flex justify-between text-muted-foreground pl-4">
                      <span className="text-xs">Primary Tech Payout:</span>
                      <span className="text-orange-600 dark:text-orange-400 text-xs">${totals.primaryTechPayout.toFixed(2)}</span>
                    </div>
                  )}
                </>
              )}
              {totals.processingFees === 0 && (totals.techCommissionAmount > 0 || totals.techPartsCost > 0) && (totals.techPartsCost > 0 || totals.additionalTechCommission > 0) && (
                <div className="flex justify-between text-muted-foreground pl-4">
                  <span className="text-xs">Primary Tech Payout:</span>
                  <span className="text-orange-600 dark:text-orange-400 text-xs">${totals.primaryTechPayout.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between pt-2 border-t border-theme">
                <span className="text-foreground font-medium">Company Profit:</span>
                <span className={`font-bold ${totals.profit >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400"}`}>
                  ${totals.profit.toFixed(2)}
                </span>
              </div>
              {payments.length > 0 && (
                <div className="flex justify-between pt-2 border-t border-theme">
                  <span className="text-muted-foreground">Payments Received:</span>
                  <span className={`font-medium ${Math.abs(totals.paymentsTotal - expectedTotal) < 0.01 ? "text-emerald-600 dark:text-emerald-400" : "text-yellow-600 dark:text-yellow-400"}`}>
                    ${totals.paymentsTotal.toFixed(2)}
                    {Math.abs(totals.paymentsTotal - expectedTotal) >= 0.01 && (
                      <span className="text-xs ml-1">
                        ({totals.paymentsTotal > expectedTotal ? "+" : ""}{(totals.paymentsTotal - expectedTotal).toFixed(2)})
                      </span>
                    )}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default CompletionSection;
